import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Spinner } from '@/components/ui/Spinner'
import { Mail, Plus, Eye, CheckCircle, XCircle, Clock, Send } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { supabase } from '@/lib/supabase'

interface EmailHistory {
  id: string
  to_email: string
  to_name: string | null
  subject: string
  content: string
  status: string | null
  sent_at: string | null
  delivered_at: string | null
  opened_at: string | null
  error_message: string | null
  created_at: string
}

interface ClientEmailsTabProps {
  clientId: string
  onSendEmail: () => void
}

const statusVariants: Record<string, 'mint' | 'gold' | 'brown' | 'red' | 'default'> = {
  pending: 'default',
  sent: 'gold',
  delivered: 'mint',
  opened: 'mint',
  failed: 'red',
  bounced: 'red',
}

const statusLabels: Record<string, string> = {
  pending: 'En attente',
  sent: 'Envoyé',
  delivered: 'Délivré',
  opened: 'Ouvert',
  failed: 'Échoué',
  bounced: 'Rejeté',
}

const statusIcons: Record<string, React.ReactNode> = {
  pending: <Clock className="w-4 h-4" />,
  sent: <Send className="w-4 h-4" />,
  delivered: <CheckCircle className="w-4 h-4" />,
  opened: <Eye className="w-4 h-4" />,
  failed: <XCircle className="w-4 h-4" />,
  bounced: <XCircle className="w-4 h-4" />,
}

export function ClientEmailsTab({ clientId, onSendEmail }: ClientEmailsTabProps) {
  const [emails, setEmails] = useState<EmailHistory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedEmail, setSelectedEmail] = useState<EmailHistory | null>(null)

  useEffect(() => {
    loadEmails()
  }, [clientId])

  async function loadEmails() {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('email_history')
        .select('*')
        .eq('related_type', 'client')
        .eq('related_id', clientId)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      setEmails(data || [])
    } catch (err) {
      console.error('Error loading emails:', err)
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        <p>{error}</p>
      </div>
    )
  }

  if (emails.length === 0) {
    return (
      <EmptyState
        icon={<Mail className="w-12 h-12" />}
        title="Aucun email"
        description="Aucun email n'a encore été envoyé à ce client."
        action={
          <Button leftIcon={<Plus className="w-4 h-4" />} onClick={onSendEmail}>
            Envoyer un email
          </Button>
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" leftIcon={<Plus className="w-4 h-4" />} onClick={onSendEmail}>
          Nouvel email
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Liste des emails */}
        <Card padding="none">
          <div className="p-4 border-b border-beige-200">
            <h3 className="font-semibold text-brown-900">
              Historique des emails ({emails.length})
            </h3>
          </div>
          <div className="divide-y divide-beige-200 max-h-[600px] overflow-y-auto">
            {emails.map((email) => (
              <div
                key={email.id}
                className={`p-4 cursor-pointer hover:bg-beige-50 transition-colors ${
                  selectedEmail?.id === email.id ? 'bg-beige-50' : ''
                }`}
                onClick={() => setSelectedEmail(email)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="font-medium text-brown-900 truncate">
                      {email.subject}
                    </p>
                    <p className="text-sm text-brown-500">
                      {formatDate(email.created_at)}
                    </p>
                  </div>
                  <Badge variant={statusVariants[email.status || 'pending']}>
                    <span className="flex items-center gap-1">
                      {statusIcons[email.status || 'pending']}
                      {statusLabels[email.status || 'pending']}
                    </span>
                  </Badge>
                </div>
                {email.error_message && (
                  <p className="text-xs text-red-600 mt-1">
                    {email.error_message}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Détail de l'email sélectionné */}
        {selectedEmail ? (
          <Card>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-brown-900 mb-4">
                  Détails de l'email
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-brown-500">Sujet</p>
                    <p className="font-medium text-brown-900">
                      {selectedEmail.subject}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-brown-500">Destinataire</p>
                    <p className="text-brown-900">
                      {selectedEmail.to_name || selectedEmail.to_email}
                    </p>
                    <p className="text-sm text-brown-500">
                      {selectedEmail.to_email}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-brown-500">Statut</p>
                    <Badge variant={statusVariants[selectedEmail.status || 'pending']}>
                      <span className="flex items-center gap-1">
                        {statusIcons[selectedEmail.status || 'pending']}
                        {statusLabels[selectedEmail.status || 'pending']}
                      </span>
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-brown-500">Envoyé le</p>
                    <p className="text-brown-900">
                      {selectedEmail.sent_at
                        ? formatDate(selectedEmail.sent_at)
                        : 'Non envoyé'}
                    </p>
                  </div>
                  {selectedEmail.delivered_at && (
                    <div>
                      <p className="text-sm text-brown-500">Délivré le</p>
                      <p className="text-brown-900">
                        {formatDate(selectedEmail.delivered_at)}
                      </p>
                    </div>
                  )}
                  {selectedEmail.opened_at && (
                    <div>
                      <p className="text-sm text-brown-500">Ouvert le</p>
                      <p className="text-brown-900">
                        {formatDate(selectedEmail.opened_at)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-beige-200 pt-4">
                <p className="text-sm text-brown-500 mb-2">Message</p>
                <div
                  className="prose prose-sm max-w-none text-brown-900 p-3 bg-beige-50 rounded-lg"
                  dangerouslySetInnerHTML={{ __html: selectedEmail.content }}
                />
              </div>

              {selectedEmail.error_message && (
                <div className="border-t border-beige-200 pt-4">
                  <p className="text-sm text-brown-500 mb-2">Erreur</p>
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">
                      {selectedEmail.error_message}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        ) : (
          <Card>
            <div className="flex items-center justify-center py-12 text-brown-400">
              <div className="text-center">
                <Mail className="w-12 h-12 mx-auto mb-3" />
                <p>Sélectionnez un email pour voir les détails</p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}
